import { useCallback, useMemo, useState } from "react";
import {
  BrandGate,
  CatalogProduct,
  HomepageSingleCategoryProduct,
  WishlistProduct,
} from "src/types/graphql.d";
import { CarouselHeader } from "src/molecules/CarouselHeader";
import { Carousel } from "src/molecules/Carousel";
import { BREAKPOINTS } from "src/breakpoints";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { LAST_TILE_MEDIA } from "src/molecules/ProductCard";
import { SEGMENT_LIST_FORMAT, SEGMENT_LIST_TYPE } from "src/segment";
import { useImageHeight } from "src/hooks/useImageHeight";
import { CarouselOverlay } from "src/molecules/CarouselOverlay";
import { CarouselMarginSize } from "src/molecules/Carousel/Carousel.styled";
import { ProductCarouselInfoText } from "./ProductCarouselInfoText";
import {
  StyledCarouselContainer,
  StyledProductCarousel,
} from "./ProductCarousel.styled";
import { ConditionalWrapper } from "src/atoms/ConditionalWrapper";
import FavouritesSizeSelector from "src/molecules/FavouritesSizeSelector/FavouritesSizeSelector";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { GatedPopupTrackingProps } from "src/types/GatedPopup";
import { GATED_POPUP_REDIRECT_DATA } from "src/constants/gatedItem";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { ProductRedirect } from "src/organisms/ProductCardWishlist/ProductCardWishlist";
import type { CarouselTrackingData } from "src/tracking/utils/types";
import { trackCarouselViewedEvent } from "src/tracking/events/carousel-viewed";
import { trackCarouselItemSelectedEvent } from "src/tracking/events/carousel-item-selected";
import { trackCarouselViewAllSelectedEvent } from "src/tracking/events/carousel-view-all-selected";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { getRecommendationsPodAttributes } from "src/utils/constructor-beacon";

export type CarouselProduct =
  | CatalogProduct
  | HomepageSingleCategoryProduct
  | WishlistProduct;

export type AllProduct =
  | CatalogProduct
  | HomepageSingleCategoryProduct
  | {
      slug: string;
      id: string;
      name: string;
      brand: {
        name: string;
        gate?: BrandGate;
      };
      thumbnail: string;
      gate?: BrandGate;
    };

// Mirrors the slide counts Carousel falls back to. See
// docs/tech/responsive-images.md.
const DEFAULT_SLIDES = { mobile: 1, tablet: 2, desktop: 3 };
const CONTENT_MAX_WIDTH = 1264;

function carouselImageSizes(
  slidesMobile = DEFAULT_SLIDES.mobile,
  slidesTablet = DEFAULT_SLIDES.tablet,
  slidesDesktop = DEFAULT_SLIDES.desktop
): string {
  const share = (slides: number) => `${Math.ceil(100 / slides)}vw`;

  return [
    `(max-width: ${BREAKPOINTS.sm}) ${share(slidesMobile)}`,
    `(max-width: ${BREAKPOINTS.lg}) ${share(slidesTablet)}`,
    `${Math.ceil(CONTENT_MAX_WIDTH / slidesDesktop)}px`,
  ].join(", ");
}

interface ProductCarouselProps {
  title?: string;
  subTitle?: string;
  headerLinkHref?: string;
  headerLinkAs?: string;
  products?: CarouselProduct[] | null;
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  zoomImageOnHover?: boolean;
  shouldReset?: boolean;
  showInfoText?: boolean;
  marginSize?: CarouselMarginSize;
  marginSizeMobile?: CarouselMarginSize;
  titleForTracking?: string;
  overlayBgColor?: string;
  genderId: string;
  allowAddToCart?: boolean;
  addToCartShortLabel?: boolean;
  responseSourceType?: string | null;
  trackingData?: GatedPopupTrackingProps;
  hideViewAll?: boolean;
  carouselTrackingData?: CarouselTrackingData;
  /** A pod id marks this carousel as a recommendation pod for the beacon, whichever engine served it. */
  cnstrcPodId?: string;
  /** Result id, seed items and strategy id enrich the beacon only when the backend sent them. */
  cnstrcResultId?: string;
  cnstrcSeedItems?: string;
  cnstrcStrategyId?: string;
}

function ProductCarousel({
  title,
  subTitle,
  headerLinkHref: rawHeaderLinkHref,
  headerLinkAs,
  products,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  zoomImageOnHover = true,
  shouldReset,
  showInfoText = false,
  marginSize = "l",
  marginSizeMobile,
  titleForTracking,
  overlayBgColor,
  genderId,
  allowAddToCart = false,
  addToCartShortLabel = false,
  trackingData,
  hideViewAll,
  carouselTrackingData,
  cnstrcPodId,
  cnstrcResultId,
  cnstrcSeedItems,
  cnstrcStrategyId,
}: ProductCarouselProps): JSX.Element {
  const headerLinkHref = useMemo(() => {
    if (!rawHeaderLinkHref) return rawHeaderLinkHref;

    const encodePathSegments = (path: string) =>
      path.split("/").map(encodeURIComponent).join("/");

    if (rawHeaderLinkHref.startsWith("/")) {
      const queryStart = rawHeaderLinkHref.search(/[?#]/);

      if (queryStart === -1) {
        return encodePathSegments(rawHeaderLinkHref);
      }

      return (
        encodePathSegments(rawHeaderLinkHref.slice(0, queryStart)) +
        rawHeaderLinkHref.slice(queryStart)
      );
    }

    try {
      const url = new URL(rawHeaderLinkHref);
      return `${url.origin}${encodePathSegments(url.pathname)}${url.search}${
        url.hash
      }`;
    } catch {
      return rawHeaderLinkHref;
    }
  }, [rawHeaderLinkHref]);

  const [imageElementWrapperHeight, getImageElementWrapper] = useImageHeight();
  const [open, setOpen] = useState<boolean>(false);
  const isLoggedIn = useIsLoggedIn();
  const { enableGatedHomepage } = useFeatureFlags();

  const listFormat = SEGMENT_LIST_FORMAT.carousel;
  const listId = title || titleForTracking || "";
  const listType = SEGMENT_LIST_TYPE.category;
  const { i18n } = useLingui();
  const { pageName, shopType, carouselType } =
    (trackingData as GatedPopupTrackingProps) || {};

  const { isGateEnabled, isGated } = useMemo(() => {
    const isGateEnabled =
      typeof enableGatedHomepage === "boolean" && enableGatedHomepage;

    return {
      isGateEnabled,
      isGated: isGateEnabled && !isLoggedIn,
    };
  }, [enableGatedHomepage, isLoggedIn]);

  const mappedTrackingData = useMemo(() => {
    // Carousels that hide their heading (cart, PDP) still track under
    // titleForTracking, so gate on that rather than on the displayed title.
    const trackingTitle = title || titleForTracking;

    if (
      !products ||
      !products.length ||
      !carouselTrackingData ||
      !trackingTitle
    ) {
      return;
    }

    return {
      title: trackingTitle,
      subtitle: subTitle,
      items: products,
      gated: isGated,
      ...carouselTrackingData,
    };
  }, [
    carouselTrackingData,
    products,
    title,
    titleForTracking,
    subTitle,
    isGated,
  ]);

  const handleTrackCarouselViewed = useCallback(
    (visibleIndexes: number[]) => {
      if (mappedTrackingData) {
        void trackCarouselViewedEvent({
          ...mappedTrackingData,
          visibleIndexes,
        });
      }
    },
    [mappedTrackingData]
  );

  const setRedirectData = useCallback(
    (
      type: string,
      product?: ProductRedirect,
      actionSource?: string,
      position?: number
    ) => {
      deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
      setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
        gatedActionType: type,
        numberOfItems: products?.length || 0,
        position,
        product,
        productListMeta: {
          listFormat,
          listId,
          listType,
          shopType: genderId,
        },
        actionSource: actionSource || "view_all",
      });
    },
    [genderId, listFormat, listId, listType, products?.length]
  );

  const [productSlug, setProductSlug] = useState<string | undefined>();

  const handleViewAllClick = useCallback(() => {
    if (isGated) {
      setOpen(true);

      return;
    }

    if (mappedTrackingData) {
      void trackCarouselViewAllSelectedEvent({
        ...mappedTrackingData,
      });
    }
  }, [isGated, mappedTrackingData]);

  const handleProductClick = useCallback(
    (itemId: string) => {
      if (mappedTrackingData) {
        void trackCarouselItemSelectedEvent({ ...mappedTrackingData, itemId });
      }
    },
    [mappedTrackingData]
  );

  const allProducts: AllProduct[] = useMemo(
    () =>
      products?.length
        ? isGated && !hideViewAll
          ? [
              ...products.slice(0, -1),
              {
                slug: "last-tile",
                id: "last-tile",
                name: t(i18n)`Log in`,
                brand: {
                  name: t(i18n)`Log in to unlock even more fashion brands`,
                },
                thumbnail: LAST_TILE_MEDIA.poster,
              },
            ]
          : products
        : [],
    [isGated, i18n, hideViewAll, products]
  );

  const items = useMemo(
    () =>
      (allProducts ?? []).map((product, index) => {
        return (
          <ConditionalWrapper
            key={product.slug}
            wrapper={(children) => (
              <CarouselOverlay
                key={product.id}
                link={!isGated && !hideViewAll ? headerLinkHref : undefined}
                overlayBgColor={overlayBgColor}
                onClick={!hideViewAll ? handleViewAllClick : undefined}
              >
                {children}
              </CarouselOverlay>
            )}
            needsWrapper={
              isGateEnabled &&
              isLoggedIn &&
              !hideViewAll &&
              index === (allProducts ?? []).length - 1
            }
          >
            <ProductCardWishlist
              key={product.slug}
              position={index}
              product={
                product as CatalogProduct | HomepageSingleCategoryProduct
              }
              onProductClickTracker={handleProductClick}
              centerText
              showLabels={false}
              showName={false}
              showSizes={false}
              zoomImageOnHover={zoomImageOnHover}
              showHoverBorder
              imageSizes={carouselImageSizes(
                slidesMobile,
                slidesTablet,
                slidesDesktop
              )}
              getImageElementWrapper={getImageElementWrapper}
              allowAddToCart={allowAddToCart}
              addToCartShortLabel={addToCartShortLabel}
              onAddToCart={setProductSlug}
              redirectLink={headerLinkHref}
              trackingData={trackingData}
              setProductRedirect={setRedirectData}
              cnstrcRecommendation={Boolean(cnstrcPodId)}
              cnstrcSection="Products"
              cnstrcStrategyId={cnstrcStrategyId}
            />
          </ConditionalWrapper>
        );
      }),
    [
      allProducts,
      allowAddToCart,
      addToCartShortLabel,
      getImageElementWrapper,
      handleProductClick,
      handleViewAllClick,
      headerLinkHref,
      overlayBgColor,
      setRedirectData,
      trackingData,
      zoomImageOnHover,
      isGateEnabled,
      isLoggedIn,
      isGated,
      hideViewAll,
      cnstrcPodId,
      cnstrcStrategyId,
    ]
  );

  return (
    <StyledProductCarousel
      showInfoText={showInfoText}
      {...getRecommendationsPodAttributes({
        podId: cnstrcPodId,
        resultId: cnstrcResultId,
        seedItems: cnstrcSeedItems,
        numResults: products?.length ?? 0,
      })}
    >
      {showInfoText && (
        <ProductCarouselInfoText
          title={title}
          subTitle={subTitle}
          linkHref={!isGated ? headerLinkHref : undefined}
          viewAllOnClick={handleViewAllClick}
        />
      )}
      {!showInfoText && (title || subTitle) && (
        <CarouselHeader
          title={title}
          subTitle={subTitle}
          href={!isGated && !hideViewAll ? headerLinkHref : undefined}
          as={headerLinkAs}
          isGated={isGated}
          viewAllOnClick={!hideViewAll ? handleViewAllClick : undefined}
        />
      )}
      <StyledCarouselContainer showInfoText={showInfoText}>
        <Carousel
          slidesDesktop={slidesDesktop}
          slidesTablet={slidesTablet}
          slidesMobile={slidesMobile}
          shouldReset={shouldReset}
          marginSize={marginSize}
          marginSizeMobile={marginSizeMobile}
          imageElementWrapperHeight={imageElementWrapperHeight}
          trackCarouselViewed={handleTrackCarouselViewed}
          items={items}
        />
      </StyledCarouselContainer>
      {allowAddToCart && (
        <FavouritesSizeSelector
          productSlug={productSlug}
          onClose={() => setProductSlug(undefined)}
        />
      )}
      {open && (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={headerLinkHref}
          trackingData={{
            pageName,
            shopType,
            carouselType,
            actionSource: "view_all",
            redirectTo: headerLinkHref,
          }}
          onRedirect={setRedirectData}
        />
      )}
    </StyledProductCarousel>
  );
}

export default ProductCarousel;
